import { useState } from "react";
import { X, Globe } from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

import { API } from "../config";

export default function AuthModal({ onClose, onSuccess }) {
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ email: "", password: "", confirmPassword: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const { login, register } = useAuth();
  const { t, lang } = useLanguage();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validatePassword = (password, confirmPassword) => {
    if (password.length < 8) {
      return t("auth.passwordHint");
    }
    const hasTwoLetters = (password.match(/[A-Za-z]/g) || []).length >= 2;
    const hasNumber = /\d/.test(password);
    if (!hasTwoLetters || !hasNumber) {
      return t("auth.passwordHint");
    }
    if (mode === "register" && password !== confirmPassword) {
      return t("auth.passwordConfirmError");
    }
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const pwError = validatePassword(form.password, form.confirmPassword);
    if (mode === "register" && pwError) {
      setPasswordError(pwError);
      return;
    }

    setError("");
    setPasswordError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form.email, form.name, form.password);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || t("auth.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="auth-modal"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white border-2 border-black w-full max-w-md" style={{ boxShadow: "6px 6px 0 #000" }}>
        <div className="flex items-center justify-between p-5 border-b-2 border-black bg-[#FF6600]">
          <h2 className="text-xl font-black uppercase tracking-tight text-black" style={{ fontFamily: "Chivo, sans-serif" }}>
            {mode === "login" ? t("auth.loginTitle") : t("auth.registerTitle")}
          </h2>
          <button data-testid="auth-modal-close" onClick={onClose} className="text-black hover:text-white transition-colors">
            <X size={22} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          {mode === "register" && (
            <div>
              <label className="rmd-label block mb-1">{t("auth.name")}</label>
              <input data-testid="auth-name-input" type="text" required value={form.name}
                onChange={(e) => set("name", e.target.value)} placeholder="Jānis Bērziņš"
                className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]" />
            </div>
          )}
          <div>
            <label className="rmd-label block mb-1">
              {t("auth.email")}
            </label>
            <input data-testid="auth-email-input" type="email" required
              value={form.email} onChange={(e) => set("email", e.target.value)}
              placeholder="janis@epasts.lv"
              className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]" />
          </div>
          <div>
            <label className="rmd-label block mb-1">
              {t("auth.password")}
            </label>
            <input data-testid="auth-password-input" type="password" required
              value={form.password} onChange={(e) => set("password", e.target.value)}
              placeholder="••••••••"
              className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]" />
            {mode === "register" && (
              <p className="text-xs text-gray-500 mt-1">{t("auth.passwordHint")}</p>
            )}
          </div>

          {mode === "register" && (
            <div>
              <label className="rmd-label block mb-1">{t("auth.password")}</label>
              <input data-testid="auth-confirm-password-input" type="password" required
                value={form.confirmPassword} onChange={(e) => set("confirmPassword", e.target.value)}
                placeholder="••••••••"
                className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]" />
              {passwordError && <p className="text-xs text-red-600 mt-1">{passwordError}</p>}
            </div>
          )}

          {(error || passwordError) && <div data-testid="auth-error" className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">{error || passwordError}</div>}

          <button data-testid="auth-submit-btn" type="submit" disabled={loading} className="rmd-btn-primary w-full justify-center">
            {loading ? "..." : mode === "login" ? t("auth.login") : t("auth.register")}
          </button>

          {mode === "login" && (
            <button type="button" data-testid="auth-switch-mode-btn"
              onClick={() => { setMode("register"); setError(""); }}
              className="w-full text-sm text-gray-500 hover:text-black transition-colors text-center">
              {t("auth.switchToRegister")}
            </button>
          )}
          {mode === "register" && (
            <button type="button" onClick={() => { setMode("login"); setError(""); }}
              className="w-full text-sm text-gray-500 hover:text-black transition-colors text-center">
              {t("auth.switchToLogin")}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
